import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import DocumentTitle from 'react-document-title';
import {
	Alert,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Col,
	Container,
	Form,
	Input,
	Label,
	Row,
	Progress,
	UncontrolledTooltip
} from 'reactstrap';
import { Line, Chart } from 'react-chartjs-2';
import 'chartjs-plugin-annotation';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import ReactGA from 'react-ga';
import { ma } from 'moving-averages';
import Select from 'react-select';

import {
	getAuthenticatedUserGroups,
	fetchNiNewestDate,
	resetNiNewestDate,
	fetchNiSmelterData,
	resetNiSmelterData,
	fetchNiPseudoGroups,
	resetNiPseudoGroups
} from 'actions';
import {
	fChartOptions,
	aChartOptions,
	atChartOptions,
	aThresholdStyle,
	genChartStyle,
	annotationsStem,
	annotationStem,
	plugins
} from './chartConfig';
import { Crumbs } from 'containers';
import { DateRangePopover } from 'components';
import { multiSelectStyle } from './multiSelectStyle';
import { smeltersInlineHelpContent } from './smeltersInlineHelpContent';
import { isUserFree, isUserSales } from '../../../utils';

import './Smelters.scss';

const config = {
	dateRangeDayLimit: 10,
	smoothingOptions: [
		{
			title: '2 day moving average',
			days: 2
		},
		{
			title: '4 day moving average',
			days: 4
		},
		{
			title: '7 day moving average',
			days: 7
		},
		{
			title: '14 day moving average',
			days: 14
		},
		{
			title: '21 day moving average',
			days: 21
		},
		{
			title: '30 day moving average',
			days: 30
		}
	],
	apiDateFormat: 'YYYYMMDD',
	dspDateFormat: 'YYYY-MM-DD',
	globalRegion: {
		name: 'Inactive Capacity Index',
		colour: '#FFA500',
		title: 'Global'
	},
	combinedRegion: {
		name: 'Combined index',
		colour: '#FFA500',
		title: 'Combine multiple regions'
	},
	fakePseudoGroups: [
		{
			name: 'Indonesia',
			key: 'indonesia'
		},
		{
			name: 'China',
			key: 'china'
		},
		{
			name: 'Asia and Oceania',
			key: 'asiaoceania'
		},
		{
			name: 'Europe',
			key: 'europe'
		},
		{
			name: 'Africa',
			key: 'africa'
		},

		{
			name: 'Americas',
			key: 'americas'
		}
	]
};

class NiSmelters extends Component {
	state = {
		groupType: 'default',
		global: true,
		regions: [],
		gradeRegions: [],
		niTypeRegions: [],
		countryRegions: [],
		selectedRegions: [],
		compareCombine: '',
		showDispersion: false,
		showActiveTonnage: false,
		dateMin: moment.utc('2016-03-01'),
		dateMax: moment.utc(),
		dateFrom: moment.utc(),
		dateTo: moment.utc(),
		smoothingDays: 7,
		isInitDateRange: false,
		shortDateRange: false,
		showDataWarning: false,
		canDownload: false
	};

	async componentDidMount() {
		plugins.forEach((plugin) => {
			//register plugin if it is not already registered
			if (!Chart.pluginService._plugins.includes(plugin)) {
				Chart.pluginService.register(plugin);
			}
		});

		this.props.fetchNiPseudoGroups({ ute: false });
		this.props.fetchNiNewestDate();
		ReactGA.pageview('/ni/smelters', null, 'Ni Smelter Operations');
	}

	componentDidUpdate(
		{ niNewestDate: prevNiNewestDate },
		{ isInitDateRange: prevIsInitDateRange }
	) {
		const { niNewestDate } = this.props;
		const { isInitDateRange } = this.state;

		//fetchNiNewestDate requested in ComponentDidMount(), ensure newestDate is received
		if (niNewestDate === '') {
			return;
		}

		//Date setting
		if (niNewestDate !== prevNiNewestDate) {
			this.updateDateHandling({ niNewestDate });
		}

		//once date range is set, fetch data
		if (isInitDateRange !== prevIsInitDateRange) {
			this.filterData();
		}
	}

	componentWillUnmount() {
		this.props.resetNiSmelterData();
		this.props.resetNiNewestDate();
		this.props.resetNiPseudoGroups();
	}

	async updateDateHandling({ niNewestDate }) {
		const { authenticatedUserGroups: authGroups } =
			await getAuthenticatedUserGroups();

		const maxDate = moment.utc(niNewestDate);

		let downloadState = {};

		//for enterprise user, savant-premium-plus, savant-premium
		let dateState = {
			dateFrom: maxDate.clone().subtract(1, 'y').subtract(1, 'd'),
			dateTo: maxDate.clone().subtract(1, 'd'),
			dateMax: maxDate.clone().subtract(1, 'd')
		};

		if (isUserSales(authGroups, 'ni')) {
			dateState = {
				...dateState,
				dateTo: maxDate.clone().subtract(4, 'w').subtract(1, 'd'),
				dateMax: maxDate.clone().subtract(4, 'w').subtract(1, 'd')
			};
		} else if (isUserFree(authGroups, 'ni')) {
			dateState = {
				...dateState,
				dateTo: maxDate.clone().subtract(4, 'w').subtract(1, 'd'),
				dateMax: maxDate.clone().subtract(4, 'w').subtract(1, 'd')
			};
		}
		if (!isUserFree(authGroups, 'ni')) {
			downloadState = {
				canDownload: true
			};
		}
		this.setState({
			...dateState,
			...downloadState,
			isInitDateRange: true
		});
	}

	// Radio button click for combine/compare
	async onRadioBtnClick(rSelected) {
		await this.setState({ ...rSelected });
	}

	toggleIndex() {
		const { showDispersion } = this.state;
		this.setState({
			showDispersion: !showDispersion,
			showActiveTonnage: false
		});
	}
	toggleActiveTonnage() {
		const { showActiveTonnage } = this.state;
		this.setState({ showActiveTonnage: !showActiveTonnage });
	}

	// Global button click
	async onGlobalBtnClick() {
		const { global, groupType } = this.state;
		let newState = {};
		//reset regions when switching to global, but only for current view
		if (!global) {
			if (groupType === 'default') {
				newState = { regions: [] };
			} else if (groupType === 'grade') {
				newState = { gradeRegions: [] };
			} else if (groupType === 'ni_type') {
				newState = { niTypeRegions: [] };
			} else if (groupType === 'country') {
				newState = { countryRegions: [] };
			}
			newState = { ...newState, selectedRegions: [] };
		}

		//toggle global
		newState = { ...newState, global: !global };

		await this.setState(newState);
		this.filterData();
	}

	// region buttons
	async onRegionBtnClick({ prop, val }) {
		//existing regions
		let regions = this.state[prop];
		const oldRegionsCount = regions.length;

		if (regions.includes(val)) {
			regions = regions.filter((item) => item !== val);
		} else {
			regions = [...regions, val];
		}
		const sortedRegions = this.sortRegions(regions);

		let newState = {
			[prop]: sortedRegions,
			selectedRegions: sortedRegions,
			global: false
		};

		if (sortedRegions.length <= 1) {
			newState = { ...newState, compareCombine: '' };
		} else if (oldRegionsCount <= 1) {
			//regions count increased
			newState = { ...newState, compareCombine: 'combine' };
		}

		await this.setState(newState);
		this.filterData();
	}

	async onSelectRegions(newRegions) {
		const { groupType, gradeRegions, niTypeRegions, countryRegions } =
			this.state;

		if (!newRegions) {
			newRegions = [];
		}

		let oldRegionsCount = 0;
		let newState = {};

		if (groupType === 'grade') {
			oldRegionsCount = gradeRegions.length;
			newState = { ...newState, gradeRegions: this.sortRegions(newRegions) };
		} else if (groupType === 'ni_type') {
			oldRegionsCount = niTypeRegions.length;
			newState = { ...newState, niTypeRegions: this.sortRegions(newRegions) };
		} else if (groupType === 'country') {
			oldRegionsCount = countryRegions.length;
			newState = { ...newState, countryRegions: this.sortRegions(newRegions) };
		}

		const sortedRegions = this.sortRegions(
			newRegions.map((region) => region.value)
		);

		newState = {
			...newState,
			global: false,
			selectedRegions: sortedRegions
		};

		if (newRegions.length <= 1) {
			newState = { ...newState, compareCombine: '' };
		} else if (oldRegionsCount <= 1) {
			//regions count increased
			newState = { ...newState, compareCombine: 'combine' };
		}

		await this.setState(newState);
		this.filterData();
	}

	// Region toggling for the select all / none button
	async onToggleAllRegions() {
		const { regions, compareCombine } = this.state;
		const { niPseudoGroups } = this.props;
		const defaultGroups = niPseudoGroups.filter((g) => g.type === 'default');

		const newRegions =
			regions.length < defaultGroups.length
				? defaultGroups.map((g) => g.key)
				: [];
		const newCompareCombine = regions.length > 1 ? compareCombine : 'combine';
		await this.setState({
			regions: newRegions,
			selectedRegions: newRegions,
			compareCombine: newRegions.length ? newCompareCombine : '',
			global: false
		});
		this.filterData();
	}

	// Handle date range selection event
	async onDateRangeSelect({ startDate, endDate }) {
		//startDate, endDate - moment objects
		await this.setState({
			dateFrom: startDate,
			dateTo: endDate,
			shortDateRange: endDate.diff(startDate, 'days') < config.dateRangeDayLimit
		});
		!this.state.shortDateRange && this.filterData();
	}

	//sort plain array of regions' names or array of region objects
	sortRegions = (regions) => {
		const { niPseudoGroups } = this.props;
		const { groupType } = this.state;
		if (!regions.length) {
			return regions;
		}
		const regionOrder = niPseudoGroups
			.filter((g) => g.type === groupType)
			.map((g) => g.key);

		return regionOrder.reduce((acc, r) => {
			let filtered = [];
			filtered = regions.filter((elem) => {
				return Object.keys(elem).length && Object.keys(elem).includes('value')
					? elem['value'] === r
					: elem === r;
			});

			if (filtered.length) {
				acc.push(filtered[0]);
			}
			return acc;
		}, []);
	};

	// InputSelect for the smoothing selection or group type
	onInputSelect = async ({ target: { value, name } }) => {
		const { regions, gradeRegions, niTypeRegions, countryRegions } = this.state;

		if (name === 'smoothingDays') {
			//values inside drop down list are strings
			this.setState({
				[name]: parseInt(value)
			});
			return;
		}

		await this.setState({
			[name]: value
		});
		if (name === 'groupType') {
			let selectedRegions = [];

			if (value === 'default') {
				selectedRegions = [...selectedRegions, ...regions];
			} else if (value === 'grade') {
				selectedRegions = [
					...selectedRegions,
					...gradeRegions.map((region) => region.value)
				];
			} else if (value === 'ni_type') {
				selectedRegions = [
					...selectedRegions,
					...niTypeRegions.map((region) => region.value)
				];
			} else if (value === 'country') {
				selectedRegions = [
					...selectedRegions,
					...countryRegions.map((region) => region.value)
				];
			}
			const global = selectedRegions.length ? false : true;
			await this.setState({
				selectedRegions: selectedRegions,
				global: global
			});
			this.filterData();
		}
	};

	// Run action creator to fetch smelter data based on filter form state
	filterData() {
		const { global, dateFrom, dateTo, selectedRegions } = this.state;
		if (global || selectedRegions.length > 0) {
			this.props.resetNiSmelterData();
			this.props.fetchNiSmelterData({
				regions: global ? 'global' : selectedRegions.toString(),
				dateFrom: dateFrom.format(config.apiDateFormat),
				dateTo: dateTo.format(config.apiDateFormat)
			});
		} else if (!global && !selectedRegions.length > 0) {
			this.props.resetNiSmelterData();
		}

		ReactGA.event({
			category: 'niSmelterIndex',
			action: 'data displayed',
			label: `From: ${dateFrom.format(config.apiDateFormat) || 'nil'} | To: ${
				dateTo.format(config.apiDateFormat) || 'nil'
			}`
		});
	}

	getChartType() {
		const { dateFrom, dateTo, showDispersion, showActiveTonnage } = this.state;
		const days = dateTo.diff(dateFrom, 'd');
		const type = showDispersion ? 'a' : showActiveTonnage ? 'c' : 'f';
		let size = '';

		if (days <= 50) {
			size = 'sm';
		} else if (days > 50 && days <= 100) {
			size = 'md';
		} else if (days > 100 && days <= 200) {
			size = 'lg';
		} else if (days > 200) {
			size = 'xl';
		} else if (days > 300) {
			size = 'xxl';
		}
		return { size, type };
	}

	// Build the card header element for display
	buildCardHeader() {
		const { compareCombine, global, showDispersion, showActiveTonnage } =
			this.state;

		let title = showDispersion
			? 'Activity Dispersion:'
			: showActiveTonnage
				? 'Active Tonnage:'
				: 'Inactive Capacity:';
		if (global) {
			title = `${title} ${config.globalRegion.title}`;
		} else if (compareCombine === 'combine') {
			title = `${title} ${config.combinedRegion.title}`;
		} else if (compareCombine === 'compare') {
			title = `${title} Compare multiple regions`;
		} else {
			title = `${title} One region`;
		}

		return (
			<CardHeader>
				<i className="fa fa-line-chart" aria-hidden="true" /> {title}
			</CardHeader>
		);
	}

	buildRegionButton({ id, name, title, tooltip = '', disabled = false }) {
		const { regions } = this.state;

		return (
			<Fragment key={`region${name}`}>
				<Button
					id={id}
					outline
					size="sm"
					color="secondary"
					onClick={() => this.onRegionBtnClick({ prop: 'regions', val: name })}
					active={regions.includes(name)}
					aria-label={title}
					className="light-active-border"
					disabled={disabled}
				>
					{title}
				</Button>
				{tooltip !== '' && (
					<UncontrolledTooltip
						placement="top"
						delay={{ show: 1500, hide: 500 }}
						target={id}
						dangerouslySetInnerHTML={{ __html: tooltip }}
					/>
				)}
			</Fragment>
		);
	}

	buildGradeButton({ id, name, title, tooltip = '', disabled = false }) {
		const { gradeRegions } = this.state;

		return (
			<Fragment key={`grade${name}`}>
				<Button
					id={id}
					outline
					size="sm"
					color="secondary"
					onClick={() =>
						this.onRegionBtnClick({ prop: 'gradeRegions', val: name })
					}
					active={gradeRegions.includes(name)}
					aria-label={title}
					className="light-active-border"
					disabled={disabled}
				>
					{title}
				</Button>
				{tooltip !== '' && (
					<UncontrolledTooltip
						placement="top"
						delay={{ show: 1500, hide: 500 }}
						target={id}
						dangerouslySetInnerHTML={{ __html: tooltip }}
					/>
				)}
			</Fragment>
		);
	}

	buildNiTypeButton({ id, name, title, tooltip = '', disabled = false }) {
		const { niTypeRegions } = this.state;

		return (
			<Fragment key={`ni_type${name}`}>
				<Button
					id={id}
					outline
					size="sm"
					color="secondary"
					onClick={() =>
						this.onRegionBtnClick({ prop: 'niTypeRegions', val: name })
					}
					active={niTypeRegions.includes(name)}
					aria-label={title}
					className="light-active-border"
					disabled={disabled}
				>
					{title}
				</Button>
				{tooltip !== '' && (
					<UncontrolledTooltip
						placement="top"
						delay={{ show: 1500, hide: 500 }}
						target={id}
						dangerouslySetInnerHTML={{ __html: tooltip }}
					/>
				)}
			</Fragment>
		);
	}

	buildCsvDownloadButton() {
		const {
			global,
			shortDateRange,
			regions,
			gradeRegions,
			niTypeRegions,
			countryRegions,
			groupType,
			canDownload
		} = this.state;
		const { niSmelterData } = this.props;

		if (
			(global ||
				(groupType === 'default' && regions.length > 0) ||
				(groupType === 'ni_type' && niTypeRegions.length > 0) ||
				(groupType === 'grade' && gradeRegions.length > 0) ||
				(groupType === 'country' && countryRegions.length > 0)) &&
			!shortDateRange &&
			niSmelterData &&
			niSmelterData.length !== 0 &&
			canDownload
		) {
			let { csvHeaders, csvData, csvFileName } = this.getCsvDataFromProps();

			return (
				<CSVLink
					id="buttonDownload"
					filename={csvFileName}
					headers={csvHeaders}
					data={csvData}
					className="btn btn-success mb-3 pull-right btn-sm"
					onClick={() => {
						ReactGA.event({
							category: 'niSmelterIndex',
							action: 'data downloaded',
							label: csvFileName
						});
						return true;
					}}
				>
					<i className="fa fa-download mr-2" aria-hidden="true" /> Download this
					data
				</CSVLink>
			);
		}

		return (
			<Button
				id="buttonDownload"
				color="success"
				disabled
				className="mb-3 pull-right"
				size="sm"
			>
				<i className="fa fa-download mr-2" aria-hidden="true" /> Download this
				data
			</Button>
		);
	}

	// Build the filter form
	buildFilterForm() {
		const {
			niPseudoGroups = [],
			hasAdvancedGroups = false,
			gradeGroups,
			niTypeGroups,
			countryGroups,
			niNewestDate
		} = this.props;
		const {
			dateMin,
			dateMax,
			dateFrom,
			dateTo,
			compareCombine,
			showDispersion,
			showActiveTonnage,
			global,
			groupType,
			regions,
			countryRegions,
			selectedRegions
		} = this.state;

		const regionToggleHidden = niPseudoGroups.length <= 2;
		const regionAllCopy =
			regions.length ===
			niPseudoGroups.filter(({ type }) => type === 'default').length
				? 'None'
				: 'All';

		const orderedKeys = config.fakePseudoGroups.map(({ key }) => key);

		return (
			<Form>
				<Container fluid className="px-0 container-width">
					<Row noGutters>
						<Col xs="12" sm="12">
							{hasAdvancedGroups && (
								<Label for="groupType" className="mb-2 mr-3 align-top">
									<span className="sr-only">Geographical filtering type</span>
									<Input
										id="groupType"
										name="groupType"
										type="select"
										bsSize="sm"
										className="pointered"
										defaultValue={groupType}
										onChange={this.onInputSelect}
									>
										<option value="default">Region filtering</option>
										<option value="grade">Metal grade filtering</option>
										<option value="ni_type">CN/ID Nickel type</option>
										<option value="country">Country filtering</option>
									</Input>
								</Label>
							)}

							<Button
								id="regionGlobal"
								outline
								size="sm"
								color="secondary"
								active={global}
								aria-label="global"
								className="mb-2 light-active-border"
								onClick={() => this.onGlobalBtnClick()}
							>
								Global
							</Button>

							{groupType === 'default' && niPseudoGroups.length === 0 && (
								<ButtonGroup className="mb-2 ml-3">
									{config.fakePseudoGroups.map(({ name, key }) => {
										return this.buildRegionButton({
											id: `region${key}`,
											name: key,
											title: name,
											disabled: true
										});
									})}
								</ButtonGroup>
							)}
							{groupType === 'default' && niPseudoGroups.length > 0 && (
								<ButtonGroup className="mb-2 ml-3">
									{niPseudoGroups
										.filter(({ type }) => type === 'default')
										.sort(
											(a, b) =>
												orderedKeys.indexOf(a.key) - orderedKeys.indexOf(b.key)
										)
										.map(({ name, key, tooltip = '' }) => {
											return this.buildRegionButton({
												id: `region${key}`,
												name: key,
												title: name,
												tooltip
											});
										})}
								</ButtonGroup>
							)}

							{groupType === 'default' && (
								<Button
									id="regionAll"
									color="primary"
									size="sm"
									onClick={() => this.onToggleAllRegions()}
									className="mb-2 ml-1"
									style={{ width: '54px' }}
									disabled={global}
									hidden={regionToggleHidden}
								>
									{regionAllCopy}
								</Button>
							)}
							{groupType === 'grade' && (
								<ButtonGroup className="mb-2 ml-3">
									{gradeGroups.map(({ label, value, tooltip = '' }) => {
										return this.buildGradeButton({
											id: `grade${value}`,
											name: value,
											title: label,
											tooltip
										});
									})}
								</ButtonGroup>
								// <Select
								// 	isMulti
								// 	options={gradeGroups}
								// 	value={gradeRegions}
								// 	onChange={(val) => this.onSelectRegions(val)}
								// 	className="react-select-container d-inline-block mb-2 ml-3"
								// 	classNamePrefix="react-select"
								// 	placeholder="Select Chinese regions..."
								// 	styles={multiSelectStyle}
								// />
							)}
							{groupType === 'ni_type' && (
								<ButtonGroup className="mb-2 ml-3">
									{niTypeGroups.map(({ label, value, tooltip = '' }) => {
										return this.buildNiTypeButton({
											id: `ni_type${value}`,
											name: value,
											title: label,
											tooltip
										});
									})}
								</ButtonGroup>
							)}
							{groupType === 'country' && (
								<Select
									isMulti
									options={countryGroups}
									value={countryRegions}
									onChange={(val) => this.onSelectRegions(val)}
									className="react-select-container d-inline-block mb-2 ml-3"
									classNamePrefix="react-select"
									placeholder="Select countries..."
									styles={multiSelectStyle}
								/>
							)}
						</Col>
					</Row>
					<Row noGutters>
						<Col xs="12" sm="12">
							<DateRangePopover
								id="dateSelector"
								startDate={dateFrom}
								endDate={dateTo}
								minDate={dateMin}
								maxDate={dateMax}
								newestDate={niNewestDate}
								onDateRangeSelected={this.onDateRangeSelect.bind(this)}
							/>
							<ButtonGroup className="mb-3 mr-2 align-bottom">
								<Button
									id="buttonCombine"
									outline
									size="sm"
									color="secondary"
									onClick={() =>
										this.onRadioBtnClick({ compareCombine: 'combine' })
									}
									active={compareCombine === 'combine'}
									disabled={selectedRegions.length <= 1}
									aria-label="Click to combine smelter data"
								>
									Combine
								</Button>
								<Button
									id="buttonCompare"
									outline
									size="sm"
									color="secondary"
									onClick={() =>
										this.onRadioBtnClick({ compareCombine: 'compare' })
									}
									active={compareCombine === 'compare'}
									disabled={selectedRegions.length <= 1}
									aria-label="Click to compare smelter data"
								>
									Compare
								</Button>
							</ButtonGroup>
							<Button
								id="buttonCapacityDispersion"
								className="mb-3 mr-1 align-bottom"
								outline
								size="sm"
								color="secondary"
								onClick={() => this.toggleIndex()}
								disabled={!global && !selectedRegions.length}
								aria-label="Click to switch between Capacity Index and Dispersion Index"
							>
								{showDispersion ? 'Inactive Capacity' : 'Activity Dispersion'}
							</Button>
							{!showDispersion && (
								<Button
									id="buttonActiveTonnage"
									className="mb-3 mr-1 align-bottom"
									outline
									size="sm"
									color="secondary"
									onClick={() => this.toggleActiveTonnage()}
									disabled={!global && !selectedRegions.length}
									aria-label="Click to display Active Tonnage"
								>
									{showActiveTonnage ? 'Inactive Capacity' : 'Active Tonnage'}
								</Button>
							)}
							<Label for="smoothingDays" className="mb-3 mr-2 smoothing-select">
								<span className="sr-only">Smoothing options</span>
								<Input
									id="smoothingDays"
									name="smoothingDays"
									type="select"
									bsSize="sm"
									className="ml-2 pointered smoothingOptions"
									value={this.state.smoothingDays}
									onChange={this.onInputSelect}
								>
									<option value="0">No data smoothing</option>
									{config.smoothingOptions.map(({ title, days }, idx) => {
										return (
											<option key={`smooth${idx}`} value={days}>
												{title}
											</option>
										);
									})}
								</Input>
							</Label>
							{this.buildCsvDownloadButton()}
						</Col>
					</Row>
				</Container>
			</Form>
		);
	}

	// Build csv data from the props for the download feature
	getCsvDataFromProps() {
		const {
			niSmelterData: { compare = [], combine = [] },
			niPseudoGroups
		} = this.props;
		let { dateFrom, dateTo } = this.state;

		// Initialise our csv data
		let csvFileName = `smelter-data-${dateFrom.format(
			config.apiDateFormat
		)}-${dateTo.format(config.apiDateFormat)}.csv`;
		let csvHeaders = [{ label: 'Date', key: 't' }];
		let csvData = [];

		compare.forEach(({ name: key, data }, i) => {
			const name =
				key !== 'global'
					? niPseudoGroups.find((pg) => pg['key'] === key)['name']
					: 'Global';
			data.forEach(({ t, f, a, c }, j) => {
				// Add the time column value to the csv data if this is the first region iteration
				// or if we've somehow got a frame error from the api data and the row is missing
				if (i === 0 || !csvData[j]) {
					csvData[j] = {
						t: moment.utc(t).format(config.dspDateFormat)
					};
				}

				// Generate the region-based f and σ column names
				let namefkey = `${name} Inactive Capacity`;
				let nameckey = `${name} Active Tonnage [kt]`;
				let nameakey = `${name} Activity Dispersion`;

				// Add the column names to the csv headers if this is the first data iteration
				if (j === 0) {
					csvHeaders = [
						...csvHeaders,
						...[
							{
								label: namefkey,
								key: namefkey
							},
							{
								label: nameckey,
								key: nameckey
							},
							{
								label: nameakey,
								key: nameakey
							}
						]
					];
				}

				// Set the f column on the csv data
				csvData[j][namefkey] = f;
				csvData[j][nameckey] = c;
				csvData[j][nameakey] = a;
			});
		});

		if (compare.length > 1) {
			combine.forEach(({ f, a, c }, k) => {
				// Generate the f column names
				let namefkey = `Combined Inactive Capacity`;
				let nameckey = `Combined Active Tonnage`;
				let nameakey = `Combined Activity Dispersion`;

				// Add the column names to the csv headers if this is the first data iteration
				if (k === 0) {
					csvHeaders = [
						...csvHeaders,
						...[
							{
								label: namefkey,
								key: namefkey
							},
							{
								label: nameckey,
								key: nameckey
							},
							{
								label: nameakey,
								key: nameakey
							}
						]
					];
				}

				// Set the f column on the csv data
				csvData[k][namefkey] = f;
				csvData[k][nameckey] = c;
				csvData[k][nameakey] = a;
			});
		}

		return {
			csvFileName,
			csvHeaders,
			csvData
		};
	}

	buildRegionSelectError() {
		return (
			<Alert color="warning" className="p-2 mb-1">
				Please select at least one region.
			</Alert>
		);
	}

	buildShortDateRangeError() {
		return (
			<Alert color="warning" className="p-2 mb-1">
				Please select a date range of at least 10 days.
			</Alert>
		);
	}

	buildLoadingMessage() {
		return (
			<Fragment>
				<div className="h6 mb-3">Chart data loading...</div>
				<Progress animated value="100" />
			</Fragment>
		);
	}

	// Apply a moving average smoothing to the data provided
	smoothSmelterData({ days, data }) {
		let fSmoothedData = data.map(({ f }) => parseFloat(f));
		fSmoothedData = ma(fSmoothedData, days);

		let cSmoothedData = data.map(({ c }) => parseFloat(c));
		cSmoothedData = ma(cSmoothedData, days);

		let aSmoothedData = data.map(({ a }) => parseFloat(a));
		aSmoothedData = ma(aSmoothedData, days);

		return data.map((item, i) => {
			if (i < days - 1) {
				return { ...item, f: null, a: null, c: null };
			}
			return {
				...item,
				f: fSmoothedData[i] && fSmoothedData[i].toFixed(1),
				c: cSmoothedData[i] && cSmoothedData[i].toFixed(1),
				a: aSmoothedData[i] && aSmoothedData[i].toFixed(1)
			};
		});
	}

	buildChartAnnotations() {
		const { niSmelterUpdates = [] } = this.props;
		const { dateFrom, dateTo } = this.state;
		const totalDays = dateTo.diff(dateFrom, 'days');

		let annotation = {};
		if (niSmelterUpdates.length > 0) {
			annotation = {
				...annotationsStem,
				annotations: []
			};

			niSmelterUpdates.forEach(({ detail, update_date }, idx) => {
				let newAnnotation = {
					...annotationStem,
					id: `anno${idx}`,
					value: new Date(update_date),
					label: {
						...annotationStem.label,
						content: detail
					}
				};

				// Position the labels away from the edges
				const pos = Math.floor(
					(moment.utc(update_date).diff(dateFrom, 'days') / totalDays) * 100
				);
				if (pos <= 13) {
					newAnnotation.label.xAdjust = -((detail.length / 2) * 6.1);
				} else if (pos >= 87) {
					newAnnotation.label.xAdjust = (detail.length / 2) * 6.1;
				}

				annotation.annotations.push(newAnnotation);
			});
		}
		return annotation;
	}

	getChartOptionsWithAnnotations(min = null, max = null) {
		const { showDispersion } = this.state;

		//setting y-axis bounds dynamically for dispersion graph
		const aChartOptionsWithRange =
			min !== null && max !== null
				? {
						...aChartOptions,
						scales: {
							...aChartOptions.scales,
							yAxes: aChartOptions.scales.yAxes.map((ya) => ({
								...ya,
								ticks: { ...ya.ticks, min: min, max: max }
							}))
						}
					}
				: aChartOptions;

		const fChartOptionsWithRange =
			min === 0 && max === 0
				? {
						...fChartOptions,
						scales: {
							...fChartOptions.scales,
							yAxes: fChartOptions.scales.yAxes.map((ya) => ({
								...ya,
								ticks: { ...ya.ticks, min: 0 }
							}))
						}
					}
				: fChartOptions;

		const options = showDispersion
			? aChartOptionsWithRange
			: fChartOptionsWithRange;

		return {
			...options,
			annotation: this.buildChartAnnotations()
		};
	}

	getSelectedDetail(selected = []) {
		const { niPseudoGroups = [] } = this.props;
		const { showActiveTonnage } = this.state;

		if (selected.length === 1 && selected[0] === 'global') {
			if (showActiveTonnage) {
				return [
					{ key: 'global', ...config.globalRegion, name: 'Active Tonnage' }
				];
			}
			return [{ key: 'global', ...config.globalRegion }];
		} else if (selected.length === 1 && selected[0] === 'combine') {
			return [{ key: 'combine', ...config.combinedRegion }];
		} else if (selected.length >= 1) {
			return selected.map((s) =>
				niPseudoGroups.find((group) => group.key === s)
			);
		}
	}

	getChartData(dataArr) {
		const {
			showDispersion,
			showActiveTonnage,
			smoothingDays: days,
			global,
			selectedRegions
		} = this.state;

		const details = this.getSelectedDetail(dataArr.map(({ name }) => name));

		//area between threshold 50% line and plot should be filled only when showing one dispersion (combine, global or one region) plot
		const fillPlot = dataArr.length === 1 ? '-1' : false;
		// Build the datasets by iterating the allRegions array and reducing it with the smelterData compare field,
		// so that the original order of the regions is maintained
		return dataArr.map(({ name, data }) => {
			// Smooth the data if required
			if (days !== 0) {
				data = this.smoothSmelterData({
					days,
					data
				});
			}

			const detail = details.find((d) => d.key === name);
			//filled point for combine
			const fCombinePointBackgroundColour =
				name === 'combine' ? { pointBackgroundColor: detail.colour } : {};
			//fStyle will be added to existing active capacity style in configuration
			const fStyle = {
				label: detail.name,
				borderColor: detail.colour,
				...fCombinePointBackgroundColour
			};

			//aStyle will be added to existing activity dispersion style in configuration;
			//threshold will be the first dataset in array with index 0
			const aStyle =
				name !== 'combine'
					? { pointBackgroundColor: '#FFFFFF', fill: fillPlot }
					: { label: 'Combined index' };
			let addStyle = {};
			//will be added to existing activity dispersion style when multiple regions are selected
			if (
				showDispersion &&
				name !== 'combine' &&
				!global &&
				selectedRegions.length > 0
			) {
				addStyle = { ...fStyle, ...aStyle };
			} else {
				addStyle = showDispersion ? aStyle : fStyle;
			}
			return {
				data: data.map(({ t, f, a, c }) => {
					return {
						t: moment.utc(t).format(config.dspDateFormat),
						y: showDispersion ? a : showActiveTonnage ? c : f
					};
				}),
				...genChartStyle(this.getChartType()),
				...addStyle
			};
		});
	}

	getRangeY(dataset) {
		const { showDispersion } = this.state;
		const data = dataset
			.map((ds) => ds.data.map((d) => d.y))
			.reduce((arr, elem) => arr.concat(elem));
		//smoothing adds some nulls at the beginning of array, remove nulls
		const dataWithoutNull = data.filter((d) => d !== null);
		const maxValue = Math.max(...dataWithoutNull);
		const minValue = Math.min(...dataWithoutNull);
		if (!showDispersion) {
			return { minY: minValue, maxY: maxValue };
		}
		//delta = max(max(maxValue,50)-50, 50-min(50,minValue))
		const delta = Math.max(
			Math.ceil(Math.max(maxValue, 50)) - 50,
			50 - Math.floor(Math.min(50, minValue))
		);
		return { minY: 50 - delta, maxY: 50 + delta };
	}

	// Individual data lines shown for multiple regions
	buildLineChart() {
		const { niSmelterData } = this.props;
		const {
			compareCombine,
			global,
			selectedRegions,
			showDispersion,
			showActiveTonnage
		} = this.state;

		const isCombined =
			compareCombine === 'combine' && !global && selectedRegions.length > 1;
		let apiData = niSmelterData.compare;
		//compare data length gives info whether API response is received, combined data length doesn't contain that information
		if (!apiData || !apiData.length) {
			return this.buildLoadingMessage();
		}
		apiData = isCombined
			? [{ name: 'combine', data: niSmelterData.combine }]
			: apiData;

		const datasets = this.getChartData(apiData);
		const { minY, maxY } = this.getRangeY(datasets);

		//build data for horizontal line at 50% of activity dispersion plot
		if (showDispersion) {
			const threshold = {
				data: niSmelterData.combine.map((point) => ({
					t: moment.utc(point.t).format(config.dspDateFormat),
					y: 50
				})),
				...aThresholdStyle
			};
			datasets.unshift(threshold);
		}

		return (
			<Card className="mb-3">
				{this.buildCardHeader()}
				<CardBody>
					<Container
						fluid
						className="pl-0 container-width"
						style={{ paddingRight: '21px' }}
					>
						<Row noGutters className="mt-0">
							<Col xs="12" sm="12">
								<Line
									data={{ datasets }}
									options={
										showActiveTonnage
											? atChartOptions
											: this.getChartOptionsWithAnnotations(minY, maxY)
									}
									redraw
								/>
							</Col>
						</Row>
					</Container>
				</CardBody>
			</Card>
		);
	}

	render() {
		let { global, shortDateRange, selectedRegions } = this.state;

		return (
			<DocumentTitle title="SAVANT | Nickel Smelter Operations">
				<div className="content-wrapper">
					<Container fluid>
						{/* Help, crumbs and the filter form */}
						<div className="pull-right">{smeltersInlineHelpContent}</div>
						<Crumbs type="ni" path={[{ title: 'Smelter Operations' }]} />
						{this.buildFilterForm()}

						{/* Error messages */}
						{shortDateRange && this.buildShortDateRangeError()}
						{!shortDateRange &&
							!global &&
							!selectedRegions.length &&
							this.buildRegionSelectError()}

						{/* Different chart types */}
						{!shortDateRange &&
							((!global && selectedRegions.length > 0) ||
								(global && !selectedRegions.length)) &&
							this.buildLineChart()}
					</Container>
				</div>
			</DocumentTitle>
		);
	}
}

const mapStateToProps = ({
	niNewestDate,
	niPseudoGroups,
	niSmelterData,
	niSmelterUpdates
}) => {
	let gradeGroups = [];
	let niTypeGroups = [];
	let countryGroups = [];
	let hasAdvancedGroups = false;

	if (niPseudoGroups && niPseudoGroups.length > 0) {
		gradeGroups = niPseudoGroups
			.filter((grp) => {
				return grp.type === 'grade';
			})
			.map(({ key, name, tooltip = '' }) => {
				return {
					value: key,
					label: name,
					tooltip
				};
			})
			.sort((a, b) => (a.label > b.label ? 1 : -1));
		niTypeGroups = niPseudoGroups
			.filter((grp) => {
				return grp.type === 'ni_type';
			})
			.map(({ key, name, tooltip = '' }) => {
				return {
					value: key,
					label: name,
					tooltip
				};
			});

		countryGroups = niPseudoGroups
			.filter((grp) => {
				return grp.type === 'country';
			})
			.map(({ key, name }) => {
				return {
					value: key,
					label: name
				};
			})
			.sort((a, b) => (a.label > b.label ? 1 : -1));
		hasAdvancedGroups = countryGroups.length > 0 || gradeGroups.length > 0;
	}

	return {
		niNewestDate,
		niPseudoGroups,
		niSmelterData,
		niSmelterUpdates,
		gradeGroups,
		niTypeGroups,
		countryGroups,
		hasAdvancedGroups
	};
};

const mapDispatchToProps = (dispatch) => ({
	fetchNiNewestDate: () => dispatch(fetchNiNewestDate()),
	resetNiNewestDate: () => dispatch(resetNiNewestDate()),
	fetchNiSmelterData: (filterData) => dispatch(fetchNiSmelterData(filterData)),
	resetNiSmelterData: () => dispatch(resetNiSmelterData()),
	fetchNiPseudoGroups: (filterData) =>
		dispatch(fetchNiPseudoGroups(filterData)),
	resetNiPseudoGroups: () => dispatch(resetNiPseudoGroups())
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withRouter(NiSmelters));
